import { isVideoFilename, videoMime } from "../../utils";
import { mediaUrl } from "../../mediaAuth";
import { useVideoPosterFrame } from "../hooks/useVideoPosterFrame";
import type { MediaItem } from "../../types";

// Shared poster-frame media tile - extracted from RecentMemories.tsx's
// original internal RecentMemoryTile (NOT from GalleryCard.tsx, which
// carries Gallery-only select-mode/delete/favorite/Bootstrap-grid-column
// concerns that don't belong in a generic preview primitive). Video tiles
// have no server-side thumbnail (unlike photos) - silently seeks to a tiny
// offset once metadata loads, pauses once the seek lands, so a still frame
// shows without ever audibly/visibly playing.
//
// Consumers: RecentMemories.tsx (Home teaser grid), WatchTogetherScreen.tsx
// (video picker grid). Renders only the thumb + play-icon overlay - no
// label/badge/selection chrome - callers compose those around it.
export function MediaPreviewCard({ item, onView, className }: { item: MediaItem; onView: () => void; className?: string }) {
  const isVid = isVideoFilename(item.filename);
  // Lazy-gated behind IntersectionObserver (see the shared hook) rather
  // than loading every video tile in the grid concurrently on mount.
  const videoRef = useVideoPosterFrame(item.filename, isVid, "media-preview-card");

  return (
    <button type="button" className={`media-preview-tile${className ? ` ${className}` : ""}`} onClick={onView} aria-label={`View ${item.filename}`}>
      {isVid ? (
        <video ref={videoRef} className="media-preview-thumb" preload="none" muted playsInline>
          <source src={mediaUrl(`/assets/media/${item.filename}`)} type={videoMime(item.filename)} />
        </video>
      ) : (
        <img className="media-preview-thumb" src={mediaUrl(`/assets/thumbs/${item.filename}`)} alt="" loading="lazy" />
      )}
      {isVid && (
        <span className="media-preview-play" aria-hidden="true">
          <i className="bi bi-play-circle-fill" />
        </span>
      )}
    </button>
  );
}
